import React, { useEffect, useState } from "react";
import {
  getAllMatrixWithdrawals,
  getPendingMatrixClaims,
  approveMatrixClaims,
  approveMatrixClaimsManually,
  rejectMatrixClaims,
} from "../../../services/Admin";
import formatDate from "../../../utils/formatDate";
import Loader from "../../components/loader/Loader";
import Loader2 from "../../components/loader/Loader2";
import CustomTable from "../../components/custom-table/CustomTable";
import CustomTableCheckbox from "../../components/custom-table/CustomTableCheckbox";
import { toast } from "react-toastify";
import "./style.css";

function AllMatrixWithdrawals() {
  const [isLoading, setIsLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [pendingList, setPendingList] = useState([]);
  const [historyList, setHistoryList] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [activeTab, setActiveTab] = useState("request");
  const [stats, setStats] = useState({ total: 0, today: 0, totalReward: 0, totalPayout: 0 });

  const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : "-");

  const pendingColumns = [
    { Header: "S.no", accessor: "sno" },
    { Header: "User ID", accessor: "userId" },
    { Header: "Name", accessor: "userName" },
    { Header: "Email", accessor: "userEmail" },
    { Header: "Stage", accessor: "stage" },
    { Header: "Reward", accessor: "reward" },
    { Header: "Payout (90%)", accessor: "payout" },
    { Header: "Wallet Address", accessor: "walletAddress" },
    { Header: "Account Number", accessor: "accountNumber" },
    { Header: "IFSC Code", accessor: "ifscCode" },
    { Header: "Date", accessor: "date" },
  ];

  const historyColumns = [
    { Header: "S.no", accessor: "sno" },
    { Header: "User ID", accessor: "userId" },
    { Header: "Name", accessor: "userName" },
    { Header: "Email", accessor: "userEmail" },
    { Header: "Stage", accessor: "stage" },
    { Header: "Reward", accessor: "reward" },
    { Header: "Payout (90%)", accessor: "payout" },
    { Header: "Claimed At", accessor: "claimedAt" },
    { Header: "Wallet", accessor: "wallet" },
    { Header: "Txn Hash", accessor: "txHash" },
  ];

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const [historyRes, pendingRes] = await Promise.all([
        getAllMatrixWithdrawals(),
        getPendingMatrixClaims(),
      ]);

      const historyData = historyRes?.data || [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      let todayCount = 0;
      let totalReward = 0;
      let totalPayout = 0;

      historyData.forEach((r) => {
        totalReward += Number(r.rewardAmount) || 0;
        totalPayout += Number(r.payoutAmount) || 0;
        const d = new Date(r.claimedAt);
        d.setHours(0, 0, 0, 0);
        if (d.getTime() === today.getTime()) todayCount++;
      });

      const formattedHistory = historyData.map((r, i) => ({
        sno: i + 1,
        userId: r.userId || "-",
        userName: r.userName || "Unknown",
        userEmail: r.userEmail || "-",
        stage: `HM${r.hm}-P${r.part}`,
        reward: `$${(Number(r.rewardAmount) || 0).toFixed(2)}`,
        payout: `$${(Number(r.payoutAmount) || 0).toFixed(2)}`,
        claimedAt: r.claimedAt ? formatDate(r.claimedAt) : "-",
        wallet: r.walletAddress ? (
          <a
            href={`https://bscscan.com/address/${r.walletAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            title={r.walletAddress}
          >
            {shortAddr(r.walletAddress)}
          </a>
        ) : (
          "-"
        ),
        txHash: r.txnHash ? (
          <a
            href={`https://bscscan.com/tx/${r.txnHash}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            BSC Scan
          </a>
        ) : (
          "-"
        ),
      }));

      const pendingData = pendingRes?.requests || [];
      const formattedPending = pendingData.map((r, i) => ({
        sno: i + 1,
        _id: r._id,
        userId: r.userId || "-",
        userName: r.userName || "Unknown",
        userEmail: r.userEmail || "-",
        stage: `HM${r.hm}-P${r.part}`,
        reward: `$${(Number(r.rewardAmount) || 0).toFixed(2)}`,
        payout: `$${(Number(r.payoutAmount) || 0).toFixed(2)}`,
        walletAddress: r.walletAddress || "-",
        accountNumber: r.accountNumber || "-",
        ifscCode: r.ifscCode || "-",
        date: r.createdAt ? formatDate(r.createdAt) : "-",
      }));

      setHistoryList(formattedHistory);
      setPendingList(formattedPending);
      setStats({ total: historyData.length, today: todayCount, totalReward, totalPayout });
    } catch (err) {
      console.error("Error fetching matrix withdrawals:", err);
      toast.error("Failed to load matrix withdrawals");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const onSelectedRows = (selectedFlatRows) => {
    setSelectedRows(selectedFlatRows);
  };

  const handleApprove = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Approve ${selectedRows.length} matrix claim(s)?`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await approveMatrixClaims(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} approved`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error approving matrix claims");
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveManually = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Approve ${selectedRows.length} matrix claim(s) manually through bank account? No USDT will be sent on-chain.`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await approveMatrixClaimsManually(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} approved manually`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error approving matrix claims manually");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRows.length) {
      toast.warn("No rows selected");
      return;
    }
    if (!window.confirm(`Reject ${selectedRows.length} matrix claim(s)? The stage will be unlocked so the user can retry.`)) return;

    setProcessing(true);
    try {
      const payloads = selectedRows.map((row) => ({ _id: row.original._id }));
      const res = await rejectMatrixClaims(payloads);
      const processed = res?.processedPayouts?.length || 0;
      const failed = res?.failedPayouts?.length || 0;
      if (processed > 0) toast.success(`${processed} rejected`);
      if (failed > 0) {
        toast.error(`${failed} failed`);
        console.error("Failed payouts:", res.failedPayouts);
      }
      await fetchAll();
    } catch (err) {
      console.error(err);
      toast.error("Error rejecting matrix claims");
    } finally {
      setProcessing(false);
    }
  };

  if (isLoading) return <Loader />;

  return (
    <div className="investment-history">
      {processing && <Loader2 />}

      <h1 className="main-heading">Matrix Withdrawals</h1>
      <br />

      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Total Withdrawals</p>
            <h2 className="stat-value">{stats.total}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Today</p>
            <h2 className="stat-value">{stats.today}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Reward (Gross)</p>
            <h2 className="stat-value">${stats.totalReward.toFixed(2)}</h2>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-left">
            <p className="stat-label">Payout (Net)</p>
            <h2 className="stat-value">${stats.totalPayout.toFixed(2)}</h2>
          </div>
        </div>
      </div>

      <br />

      <div className="hbw-tabs">
        <button
          className={`hbw-tab${activeTab === "request" ? " active" : ""}`}
          onClick={() => setActiveTab("request")}
          type="button"
        >
          Request ({pendingList.length})
        </button>
        <button
          className={`hbw-tab${activeTab === "history" ? " active" : ""}`}
          onClick={() => setActiveTab("history")}
          type="button"
        >
          History ({historyList.length})
        </button>
      </div>

      {activeTab === "request" ? (
        <>
          <div className="button-group" style={{ margin: "1rem 0" }}>
            <button
              onClick={handleReject}
              disabled={processing || !selectedRows.length}
              style={{ background: "red", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Reject Selected
            </button>
            <button
              onClick={handleApproveManually}
              disabled={processing || !selectedRows.length}
              style={{ background: "#2563eb", marginLeft: "0.5rem", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Approve Manually
            </button>
            <button
              onClick={handleApprove}
              disabled={processing || !selectedRows.length}
              style={{ background: "green", marginLeft: "0.5rem", opacity: !selectedRows.length ? 0.6 : 1 }}
            >
              Approve Selected
            </button>
          </div>

          <CustomTableCheckbox
            columns={pendingColumns}
            data={pendingList}
            heading={`Pending Matrix Claims (${pendingList.length})`}
            onSelectedRows={onSelectedRows}
          />
        </>
      ) : (
        <CustomTable
          data={historyList}
          columns={historyColumns}
          heading={`Matrix Withdrawals History (${historyList.length})`}
        />
      )}
    </div>
  );
}

export default AllMatrixWithdrawals;
